import type {
  IAiAgent,
  IOpenAIMessage,
  IOpenAISchema,
  IResource,
  IUser,
  ICompletePromptResponse,
  IToolCall
} from '@rready/sdk';
import type { AiGateway } from '@rready/sdk/dist/gateway/AiGateway';
import type {
  IChatManager,
  IChatMessage,
  IChatSession,
  IChatUser,
  IContextManager
} from './chatTypes';
import { useUser, useMustache } from '@rready/composables';
import { aiAgentTools } from './aiTools';
import { updateTicketFieldImplementation } from './tools/updateTicketField';
import type { UpdateTicketFieldParams } from './tools/updateTicketField';
import { suggestTicketImprovementImplementation } from './tools/suggestTicketImprovement';
import type { SuggestTicketImprovementParams } from './tools/suggestTicketImprovement';

const schema: IOpenAISchema = {
  type: 'object',
  properties: {
    answer: {
      type: 'string'
    }
  },
  required: ['answer'],
  additionalProperties: false
};

type Answer = { answer: string };

function userToChatUser(user: IUser): IChatUser {
  return {
    id: user.id,
    name: useUser({ user }).displayName(),
    avatar: user.avatar,
    self: true,
    ai: false
  };
}

function agentToChatUser(
  $res: <T>(resource: IResource<T>) => T,
  agent: IAiAgent
): IChatUser {
  const res = $res(agent.resources);
  return {
    id: agent.id,
    name: res.label,
    avatar: agent.avatarUrl,
    self: false,
    ai: true
  };
}

function agentToWelcomeMessage(
  $res: <T>(resource: IResource<T>) => T,
  agent: IAiAgent
): IChatMessage {
  const res = $res(agent.messages);
  return {
    owner: agentToChatUser($res, agent),
    text: res.message,
    suggestions: res.suggestedPrompts.map((it) => ({
      text: it.label
    })),
    template: 'text',
    status: 'ok',
    dateCreated: new Date().toISOString()
  };
}

function updateAiResponse(
  chatMessage: IChatMessage,
  response: ICompletePromptResponse<Answer>
) {
  let text = response.message?.answer ?? '';

  text = text ? `${text}\n` : text;
  response.tools?.forEach((it) => {
    text += `- ${it.arguments.reason}\n`;
  });

  chatMessage.text = text;
  chatMessage.meta = { tools: response.tools };
  if (response.tools?.some(({ name }) => name === 'updateTicketField')) {
    chatMessage.template = 'action';
  } else if (
    response.tools?.some(({ name }) => name === 'suggestTicketImprovement')
  ) {
    chatMessage.template = 'copy';
  } else {
    chatMessage.template = 'text';
  }
}

export function useAiChatProvider(
  gateway: AiGateway,
  $res: <T>(resource: IResource<T>) => T,
  agent: IAiAgent,
  user: IUser,
  contextManager: IContextManager,
  chatManager: IChatManager
) {
  const aiPrePrompt = (sessionKey?: string): string => {
    const chatContext = contextManager.getContext(sessionKey);

    const prePromptTemplate = $res(agent.messages).prePrompt;
    const { replaceNested } = useMustache(prePromptTemplate);
    return replaceNested(chatContext);
  };

  const aiMessageContext = (chatSession: IChatSession): IOpenAIMessage[] => {
    const systemMessage: IOpenAIMessage = {
      role: 'system',
      content: aiPrePrompt(chatSession.key)
    };
    const historyMessages: IOpenAIMessage[] = chatSession.messages.map(
      (it) => ({
        role: it.owner.ai ? 'assistant' : 'user',
        content: it.text
      })
    );
    return [systemMessage, ...historyMessages];
  };

  const useSession = (chatSession: IChatSession) => {
    const chatUserSelf = chatSession.users.find((it) => it.self)!;
    const chatUserAi = chatSession.users.find((it) => it.ai)!;

    const sendMessage = async (message: string) => {
      const { pushMessage } = chatManager;

      pushMessage(chatSession.key, {
        owner: chatUserSelf,
        text: message,
        status: 'ok',
        template: 'text',
        dateCreated: new Date().toISOString()
      });

      const messages = aiMessageContext(chatSession);

      const availableFunctions = agent.functions.map((it) => it.type);
      const tools = Object.entries(aiAgentTools)
        .filter((it) => availableFunctions.includes(it[0]))
        .map((it) => it[1]);

      const responsePromise = gateway.completePrompt<Answer>(
        schema,
        messages,
        tools
      );

      const chatMessage: IChatMessage = {
        owner: chatUserAi,
        text: '',
        status: 'loading',
        template: 'text',
        dateCreated: new Date().toISOString()
      };
      setTimeout(async () => {
        pushMessage(chatSession.key, chatMessage);

        try {
          const response = await responsePromise;
          updateAiResponse(chatMessage, response);
          chatMessage.status = 'ok';
        } catch (e: any) {
          chatMessage.status = 'error';
        }
      }, 700);
    };

    const acceptAction = async (message: IChatMessage, sessionKey?: string) => {
      const calls = (message.meta?.tools as IToolCall[]) ?? [];
      const context = contextManager.getContext(sessionKey);
      calls.forEach((it) => {
        if (it.name === 'updateTicketField') {
          updateTicketFieldImplementation(
            it.arguments as UpdateTicketFieldParams,
            context.ticketSource
          );
        } else if (it.name === 'suggestTicketImprovement') {
          suggestTicketImprovementImplementation(
            it.arguments as SuggestTicketImprovementParams
          );
        }
      });
      contextManager.contextUpdated();
    };

    return { sendMessage, acceptAction };
  };

  const createSession = (sessionId: string) => {
    const chatUserSelf = userToChatUser(user);
    const chatUserAi = agentToChatUser($res, agent);

    const chatSession: IChatSession = {
      key: sessionId,
      title: $res(agent.resources).label,
      users: [chatUserSelf, chatUserAi],
      messages: [agentToWelcomeMessage($res, agent)]
    };
    const session = chatManager.createSession(chatSession);
    return useSession(session);
  };

  const hasSession = (sessionId: string) => {
    return chatManager.hasSession(sessionId);
  };

  const openSession = (sessionId: string) => {
    const chatSession = chatManager.loadSession(sessionId);
    return useSession(chatSession);
  };

  return { createSession, openSession, hasSession };
}
